// The end of the game.
//   Age III's civil deck running out starts Age IV (as any new age), and with it the end: everyone gets one more turn
//   in Age IV, the last player in turn order taking the last one. When Age IV begins before the first player's turn
//   gets going (the row refilled at its start, or International Agreement in their political phase), that round is the
//   last; otherwise the round is finished and one more is played. (finalRound: the round the game ends with.)
//   Nothing else changes in Age IV, except that there is no military deck to draw from.
//   After the last turn (scored and produced as usual) comes the final scoring: every Age III event still in the current
//   or future events resolves (in any order: none depends on another; ties broken as if the first player's turn), and
//   cards that score at the end of the game do (Bill Gates). The most culture wins; ties share the win.
//   A player who resigns (as their political action; not in Age IV) leaves the game: their pacts end, every war declared
//   on them scores its declarer 7 culture (the wars they declared just leave play), and the game goes on as if they were
//   never in it (turn order, the card row, the decks of the ages to come). When one player is left, the game ends and
//   they win.
// The finished game: gameState.finished, { round, standings: [{ player, name, culture, beforeFinalScoring }], winners,
// finalScoring: [reports], resigned: [names] }.
const { MilitaryCardTypes } = require('../Constants/Names/CardTypes');
const { Age } = require('../Constants/Constants');
const { FireTriggers, TriggerEvents } = require('../Effects/Effects');
const { GetAllStats, PlayerChanges } = require('../Utilities/Stats');
const { CardRef } = require('../Utilities/CardRefs');

const ActivePlayers = (gameState) => gameState.playerStates.filter(player => !player.resigned);
const FirstActive = (gameState) => ActivePlayers(gameState)[0]?.playerNumber ?? 0;
const LastActive = (gameState) => ActivePlayers(gameState).at(-1)?.playerNumber ?? 0;

// The next player still in the game after this one, and whether that starts a new round
const NextActive = (gameState, from) => {
    for (let offset = 1; offset <= gameState.playerCount; offset++)
    {
        const next = (from + offset) % gameState.playerCount;
        if (!gameState.playerStates[next].resigned)
        {
            return { next, newRound: next <= from };
        }
    }
    return { next: from, newRound: false };
};

// Age IV has begun: the round the game ends with, announced to everyone
const SetFinalRound = (gameState) => {
    if (gameState.finalRound !== null)
    {
        return;
    }
    const thisRound = gameState.currentPlayer === FirstActive(gameState);
    gameState.finalRound = thisRound ? gameState.round : gameState.round + 1;
    const last = gameState.playerStates[LastActive(gameState)].name;
    gameState.reports.push({
        kind: 'notice', name: Age.Age4, round: gameState.round,
        text: `Age III's civil deck has run out. ${thisRound
            ? 'This round is the last: everyone takes one more turn'
            : 'This round is finished, then everyone takes one more turn'}; ${last} takes the last turn.`,
        changes: [],
    });
};

// Whether this turn of the player is their last
const IsLastTurn = (gameState, playerNumber = gameState.currentPlayer) =>
    gameState.finalRound !== null && gameState.round === gameState.finalRound && playerNumber === gameState.currentPlayer;

// For each player, how their last turn stands: null (not known yet), 'next round', 'last turn' (theirs is this round,
// still to come or under way) or 'done'
const LastTurnStatus = (gameState, playerNumber) => {
    if (gameState.finalRound === null)
    {
        return null;
    }
    if (gameState.finished)
    {
        return 'done';
    }
    if (gameState.round < gameState.finalRound)
    {
        return 'next round';
    }
    return playerNumber < gameState.currentPlayer ? 'done' : 'last turn';
};

// The game is over: final scoring, then the standings
const EndGame = (gameState, { scoring = true } = {}) => {
    const before = new Map(gameState.playerStates.map(player => [player.playerNumber, player.culture]));
    const finalScoring = [];
    if (scoring)
    {
        const { ResolveEvent } = require('./EventEffects');
        // Ties are broken as if it were the first player's turn
        gameState.currentPlayer = FirstActive(gameState);
        const ageThree = (cards) => cards.filter(card => card?.type === MilitaryCardTypes.Event && card.age === Age.Age3);
        [...ageThree(gameState.currentEvents), ...ageThree(gameState.futureEvents)].forEach(card => {
            const stats = GetAllStats(gameState);
            const text = ResolveEvent(gameState, card);
            const owner = gameState.eventOwners[card.id];
            finalScoring.push({
                id: card.id, name: card.name, age: card.age, card: require('../Utilities/Converters/CardToData')(card), kind: 'finalScoring', round: gameState.round,
                revealedBy: owner !== undefined ? gameState.playerStates[owner].name : null,
                text, ...(gameState.lastRankings?.length ? { rankings: gameState.lastRankings } : {}),
                changes: PlayerChanges(stats, GetAllStats(gameState)),
            });
            gameState.lastRankings = null;
        });
        // Cards that score at the end of the game
        ActivePlayers(gameState).forEach(player => {
            const stats = GetAllStats(gameState);
            const logged = gameState.triggerLog.length;
            FireTriggers(player, TriggerEvents.GameEnd);
            const notes = gameState.triggerLog.slice(logged);
            const changes = PlayerChanges(stats, GetAllStats(gameState));
            if (notes.length > 0 || changes.length > 0)
            {
                finalScoring.push({ kind: 'finalScoring', name: 'End of the game', round: gameState.round, revealedBy: player.name,
                    text: notes.join('; ') || `${player.name} scores at the end of the game`, changes });
            }
        });
    }
    const standings = ActivePlayers(gameState)
        .map(player => ({ player: player.playerNumber, name: player.name, culture: player.culture, beforeFinalScoring: before.get(player.playerNumber) }))
        .sort((a, b) => b.culture - a.culture);
    const best = standings[0]?.culture;
    gameState.finished = {
        round: gameState.round,
        standings,
        winners: standings.filter(entry => entry.culture === best).map(entry => entry.name),
        finalScoring,
        resigned: gameState.playerStates.filter(player => player.resigned).map(player => player.name),
    };
    gameState.reports.push(...finalScoring);
    gameState.reports.push({
        kind: 'notice', name: 'Game over', round: gameState.round, changes: [],
        text: `${gameState.finished.winners.join(' and ')} win${gameState.finished.winners.length === 1 ? 's' : ''} with ${best} culture`,
    });
    gameState.decisions = [];
    gameState.phase = require('./MoveTypes').Phase.GameOver;
};

// The upcoming ages' decks, built for 'from' players, as they would be for 'to' players: the cards only the larger
// count adds are taken out (by kind and name; which copy does not matter, the decks are shuffled)
const ShrinkUpcomingDecks = (gameState, from, to) => {
    const { DeckDataFactory } = require('../Factories/DeckDataFactory');
    const CardConverter = require('../Utilities/Converters/CardConverter');
    // Compared as cards in play are named (a deck entry's name can differ, e.g. "Bonus" for Defense/Colonization)
    const key = (card) => `${card.type}|${card.name}`;
    const named = (cards) => cards.map(card => CardConverter(card) ?? card);
    // How many more of each card a deck for 'from' players has than one for 'to'
    const surplus = (build) => {
        const counts = new Map();
        named(build(from)).forEach(card => counts.set(key(card), (counts.get(key(card)) ?? 0) + 1));
        named(build(to)).forEach(card => counts.set(key(card), (counts.get(key(card)) ?? 0) - 1));
        return counts;
    };
    const remove = (cards, counts) => cards.filter(card => {
        const extra = counts.get(key(card)) ?? 0;
        if (extra > 0)
        {
            counts.set(key(card), extra - 1);
            return false;
        }
        return true;
    });
    gameState.upcomingDecks.forEach(deck => {
        const setup = gameState.setup ?? undefined;
        deck.civil = remove(deck.civil, surplus(count => DeckDataFactory.GetCivilDeck(deck.age, count, setup)));
        deck.military = remove(deck.military, surplus(count => DeckDataFactory.GetMilitaryDeck(deck.age, count, setup)));
    });
};

// Why the player cannot resign now, or null: not in the middle of something they are part of
const ResignProblem = (gameState, player) => {
    if (player.resigned)
    {
        return "You have already resigned";
    }
    if (gameState.age === Age.Age4)
    {
        return "No one can resign in Age IV";
    }
    if (gameState.auction)
    {
        return "Wait until the colonization auction is over";
    }
    const aggression = gameState.aggression;
    if (aggression && (aggression.aggressor === player.playerNumber || aggression.defender === player.playerNumber))
    {
        return "Wait until the aggression is resolved";
    }
    return null;
};

// The player leaves the game
const Resign = (gameState, player) => {
    if (LeaveGame(gameState, player, `${player.name} resigns`))
    {
        // It was their political action: their turn ends once what it set off is decided (e.g. a pact partner settling
        // the blue tokens it gave back), as the political action is resolved (see Rules.Apply)
        gameState.resignAdvance = player.playerNumber;
        gameState.politicalResolving = true;
    }
};

// A player taken out of the game from outside it, at any point, in any age (their account was deleted: see the API's
// routes/admin.js): they count as resigned. What they were part of goes on without them: their open decisions are
// dropped, in a colonization auction they pass (if they were the highest bidder, the bidding starts over, or no one
// colonizes if everyone else had passed), an aggression by or on them is called off, and on their own turn the turn
// passes on (or, on the game's very last turn, the game ends with its final scoring).
const Withdraw = (gameState, player) => {
    const number = player.playerNumber;
    const auction = gameState.auction;
    if (auction?.order.includes(number))
    {
        const theirs = (type) => gameState.decisions.some(d => d.player === number && d.type === type);
        const [asked, paying] = [theirs('colonyBid'), theirs('colonyPay')];
        gameState.decisions = gameState.decisions.filter(d => !(d.player === number && ['colonyBid', 'colonyPay'].includes(d.type)));
        if (!auction.passed.includes(number))
        {
            auction.passed.push(number);
        }
        if (auction.highest?.player === number)
        {
            auction.highest = null;
        }
        if (paying)
        {
            gameState.auction = null;
        } else if (asked) {
            require('./Colonization').AdvanceAuction(gameState, number);
        }
    }
    const aggression = gameState.aggression;
    if (aggression && (aggression.aggressor === number || aggression.defender === number))
    {
        gameState.decisions = gameState.decisions.filter(d => !['defend', 'aggressionChoice'].includes(d.type));
        const card = require('./Aggression').AggressionCard(gameState);
        require('./Aggression').Finish(gameState, [`${CardRef(card)} is called off: ${player.name} has left the game`]);
    }
    const theirTurn = gameState.currentPlayer === number;
    const lastTurn = theirTurn && IsLastTurn(gameState, number) && LastActive(gameState) === number;
    if (!LeaveGame(gameState, player, `${player.name} has left the game (counts as resigned)`))
    {
        return;
    }
    require('./Decisions').RefreshNext(gameState);
    if (lastTurn)
    {
        EndGame(gameState);
    } else if (theirTurn) {
        gameState.resignAdvance = number;
    }
};

// What resigning and withdrawing share: the player is out, their pacts and wars end, and the game goes on without them.
// False when that ended the game (one player left).
const LeaveGame = (gameState, player, text) => {
    const Pact = require('./Pact');
    const CardConverter = require('../Utilities/Converters/CardConverter');
    const number = player.playerNumber;
    player.resigned = true;
    gameState.decisions = gameState.decisions.filter(decision => decision.player !== number);
    // Their pacts end; an offer to them goes back to its offerer, one from them is discarded
    Pact.PactsOf(gameState, number).forEach(pact => Pact.CancelPact(gameState, player, pact));
    const offer = gameState.pactOffer;
    if (offer && (offer.offerer === number || offer.partner === number))
    {
        if (offer.partner === number)
        {
            gameState.playerStates[offer.offerer].militaryHand.push(CardConverter(offer.card));
        } else {
            gameState.militaryDiscard.push(CardConverter(offer.card));
            gameState.decisions = gameState.decisions.filter(decision => decision.type !== 'pactOffer');
        }
        gameState.pactOffer = null;
    }
    // Every war declared on them scores its declarer 7 culture; the wars they declared just leave play with their cards
    const notes = [];
    gameState.wars.filter(war => war.aggressor === number || war.defender === number).forEach(war => {
        if (war.defender === number)
        {
            const declarer = gameState.playerStates[war.aggressor];
            declarer.culture += 7;
            notes.push(`${declarer.name} scores 7 culture for ${CardRef(war.card)}`);
        }
        gameState.militaryDiscard.push(CardConverter(war.card));
    });
    gameState.wars = gameState.wars.filter(war => war.aggressor !== number && war.defender !== number);
    gameState.reports.push({ kind: 'notice', name: 'Resignation', round: gameState.round, changes: [],
        text: `${text}${notes.length ? `: ${notes.join('; ')}` : ''}` });

    if (ActivePlayers(gameState).length <= 1)
    {
        EndGame(gameState, { scoring: false });
        return false;
    }
    // The decks of the ages to come lose the cards a game with one player fewer would not have
    ShrinkUpcomingDecks(gameState, ActivePlayers(gameState).length + 1, ActivePlayers(gameState).length);
    return true;
};

module.exports = {
    ActivePlayers,
    FirstActive,
    LastActive,
    NextActive,
    SetFinalRound,
    IsLastTurn,
    LastTurnStatus,
    EndGame,
    ResignProblem,
    Resign,
    Withdraw,
    ShrinkUpcomingDecks,
};
